import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Send, MessageCircle, AlertCircle, Check, CheckCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpectatorSupportPage() {
  const [sessionId] = useState(() => {
    let id = localStorage.getItem('spectator_session_id');
    if (!id) {
      id = `spec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('spectator_session_id', id);
    }
    return id;
  });
  
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [spectatorName, setSpectatorName] = useState('');
  const [isNameSet, setIsNameSet] = useState(false);
  const [sending, setSending] = useState(false);
  const [supportTyping, setSupportTyping] = useState(false);
  const [supportReadAt, setSupportReadAt] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    const initConversation = async () => {
      try {
        const response = await axios.post(`${API}/spectator-support/conversation`, {
          sessionId,
          spectatorName: spectatorName || 'Spectator'
        });
        setConversation(response.data);
        
        if (response.data.spectatorName && response.data.spectatorName !== 'Spectator') {
          setSpectatorName(response.data.spectatorName);
          setIsNameSet(true);
        }
      } catch (error) {
        console.error('Failed to init conversation:', error);
      }
    };
    
    initConversation();
  }, [sessionId]);

  // Fetch messages and status
  useEffect(() => {
    if (!conversation) return;
    
    const fetchData = async () => {
      try {
        // Fetch messages
        const msgResponse = await axios.get(`${API}/spectator-support/messages/${conversation.id}`);
        setMessages(msgResponse.data.messages || []);
        
        // Mark as read (only if not closed)
        if (!conversation.closed) {
          await axios.put(`${API}/spectator-support/conversation/${conversation.id}/read`, {
            reader: 'spectator'
          });
        }
        
        // Fetch status (typing, read receipts, closed status)
        const statusResponse = await axios.get(`${API}/spectator-support/conversation/${conversation.id}/status`);
        setSupportTyping(statusResponse.data.supportTyping);
        setSupportReadAt(statusResponse.data.supportReadAt);
        
        // Update closed status if changed
        if (statusResponse.data.closed !== conversation.closed) {
          setConversation(prev => ({ ...prev, closed: statusResponse.data.closed }));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    
    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [conversation?.id, conversation?.closed]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, supportTyping]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (isTyping) => {
    if (!conversation) return;
    
    // Throttle typing updates to once per 2 seconds
    const now = Date.now();
    if (isTyping && now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    
    try {
      await axios.put(`${API}/spectator-support/conversation/${conversation.id}/typing`, {
        typer: 'spectator',
        isTyping
      });
    } catch (error) {
      console.error('Failed to send typing indicator:', error);
    }
  }, [conversation]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (e.target.value.trim()) {
      sendTypingIndicator(true);
      
      // Clear typing after 3 seconds of no input
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(false);
      }, 3000);
    } else {
      sendTypingIndicator(false);
    }
  };

  const handleSetName = async () => {
    if (!spectatorName.trim()) return;
    
    try {
      if (conversation) {
        await axios.put(`${API}/spectator-support/conversation/${conversation.id}/name`, {
          spectatorName: spectatorName.trim()
        });
      }
      setIsNameSet(true);
    } catch (error) {
      console.error('Failed to set name:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation || sending) return;
    
    // Clear typing indicator
    sendTypingIndicator(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    
    setSending(true);
    try {
      await axios.post(`${API}/spectator-support/message`, {
        conversationId: conversation.id,
        sender: 'spectator',
        message: newMessage.trim()
      });
      
      setNewMessage('');
      setSupportReadAt(null); // Reset read receipt for new message
      
      const response = await axios.get(`${API}/spectator-support/messages/${conversation.id}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Get the last message from spectator to show read receipt
  const lastSpectatorMessage = [...messages].reverse().find(m => m.sender === 'spectator');

  // Name entry screen
  if (!isNameSet) {
    return (
      <div className="h-screen flex flex-col bg-white">
        <div className="bg-black text-white p-3 text-center">
          <img src="/scorz-logo.png" alt="Scorz" className="h-8 mx-auto mb-1 invert" />
          <h1 className="text-lg font-bold">Spectator Support</h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm">
            <CardContent className="pt-6 space-y-4">
              <div className="text-center">
                <MessageCircle className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <h2 className="text-lg font-semibold">Welcome!</h2>
                <p className="text-gray-600 text-sm">Chat with our on-site support team.</p>
              </div>
              
              <div>
                <Input
                  value={spectatorName}
                  onChange={(e) => setSpectatorName(e.target.value)}
                  placeholder="Enter your name"
                  className="text-base"
                  onKeyDown={(e) => e.key === 'Enter' && handleSetName()}
                  autoFocus
                />
              </div>
              
              <Button 
                onClick={handleSetName}
                className="w-full bg-black hover:bg-gray-800 h-11"
                disabled={!spectatorName.trim()}
              >
                Start Chat
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Chat screen
  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-black text-white px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <img src="/scorz-logo.png" alt="Scorz" className="h-7 invert" />
          <div>
            <h1 className="font-bold text-sm">Spectator Support</h1>
            <p className="text-xs text-gray-400">On-site team</p>
          </div>
        </div>
        <div className="text-right text-xs">
          <p className="text-gray-400">Chatting as</p>
          <p className="font-semibold">{spectatorName}</p>
        </div>
      </div>
      
      {/* Stay on page notice */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 shrink-0">
        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
        <p className="text-xs text-amber-800">
          Please stay on this page to receive replies. You'll see when your message has been read.
        </p>
      </div>

      {/* Conversation closed notice */}
      {conversation?.closed && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3 text-center shrink-0">
          <Check className="w-5 h-5 mx-auto text-green-600 mb-1" />
          <p className="text-sm font-medium text-green-800">This conversation has been resolved</p>
          <p className="text-xs text-green-600 mt-1">
            Refresh the page to start a new conversation if you need more help.
          </p>
        </div>
      )}
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <div className="max-w-2xl mx-auto space-y-3">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">No messages yet.</p>
              <p className="text-gray-400 text-xs">Send a message to get help.</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isLastSpectatorMsg = lastSpectatorMessage?.id === msg.id;
              
              return (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'spectator' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="max-w-[80%]">
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        msg.sender === 'spectator'
                          ? 'bg-black text-white rounded-br-md'
                          : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                      }`}
                    >
                      {msg.sender === 'support' && (
                        <p className="text-xs font-semibold text-gray-500 mb-1">Support Team</p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-xs mt-1 text-gray-400">{formatTime(msg.createdAt)}</p>
                    </div>
                    
                    {/* Read receipt for last spectator message */}
                    {msg.sender === 'spectator' && isLastSpectatorMsg && (
                      <div className="flex items-center justify-end gap-1 mt-1 pr-1">
                        {supportReadAt ? (
                          <>
                            <CheckCheck className="w-3 h-3 text-blue-500" />
                            <span className="text-xs text-blue-500">Read at {formatTime(supportReadAt)}</span>
                          </>
                        ) : (
                          <>
                            <Check className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-400">Sent</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          
          {/* Typing indicator */}
          {supportTyping && (
            <div className="flex justify-start">
              <div className="bg-white text-gray-600 rounded-2xl rounded-bl-md shadow-sm px-4 py-2">
                <p className="text-xs font-semibold text-gray-500 mb-1">Support Team</p>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Message Input - disabled when conversation is closed */}
      <div className="bg-white border-t p-3 shrink-0">
        {conversation?.closed ? (
          <div className="max-w-2xl mx-auto text-center">
            <Button 
              onClick={() => window.location.reload()}
              className="bg-black hover:bg-gray-800"
            >
              Start New Conversation
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSendMessage} className="max-w-2xl mx-auto flex gap-2">
            <Input
              value={newMessage}
              onChange={handleInputChange}
              placeholder="Type your message..."
              className="flex-1"
              disabled={sending}
            />
            <Button 
              type="submit" 
              className="bg-black hover:bg-gray-800 px-4"
              disabled={!newMessage.trim() || sending}
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
